import { BACKEND_URL } from '../config/backendUrl';
/* VerificationPage — public, no auth.
 *
 * Candidate lands here from a packet's Right to Work verification step
 * (`pending_verifications` item in the portal, or a direct link). On mount,
 * calls the backend to get (and lazily create, on first open) a TrustID
 * Guest Link, then redirects the candidate's browser to TrustID's own
 * hosted page — this app never talks to TrustID's API directly, only to
 * our own backend, which is the one that talks to TrustID (backend-only
 * rule). Handles terminal/error states with no dead end back into the
 * rest of the packet.
 */
import { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import {
  ShieldCheck,
  CheckCircle2,
  AlertCircle,
  RefreshCw,
  ExternalLink,
  ChevronLeft,
  Clock,
} from 'lucide-react';

const API = BACKEND_URL;
const PRIMARY = '#2D5A27';

const STATUS_COPY = {
  pending_start: {
    title: 'Starting your Right to Work check',
    body: "We're setting up your identity verification session.",
  },
  link_created: {
    title: 'Redirecting you to TrustID',
    body: "You'll complete a quick document scan and a short liveness check on TrustID's secure site.",
  },
  opened: {
    title: 'Continue your Right to Work check',
    body: "You've started this before — pick up where you left off.",
  },
  submitted: {
    title: 'Check submitted',
    body: "We've received your submission and it's being reviewed.",
  },
  submitted_under_review: {
    title: "You're all set",
    body: 'Your Right to Work check has been submitted and is under review. Your recruiter will be in touch if anything else is needed.',
  },
  expired: {
    title: 'This link has expired',
    body: 'Please contact your recruiter for a new link.',
  },
  cancelled: {
    title: 'This check was cancelled',
    body: 'Please contact your recruiter if you believe this is a mistake.',
  },
};

export default function VerificationPage() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [redirecting, setRedirecting] = useState(false);
  const [status, setStatus] = useState(null);
  const [linkUrl, setLinkUrl] = useState(null);
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API}/api/public/trustid/verify/${token}`);
      const data = res.data || {};
      setStatus(data.status || null);
      setLinkUrl(data.link_url || null);
      setMessage(data.message || null);
    } catch (err) {
      const httpStatus = err.response?.status;
      if (httpStatus === 404) setError('This verification link could not be found.');
      else if (httpStatus === 410) setError('This verification link has expired.');
      else setError('Could not load this verification. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  // Auto-redirect to TrustID's hosted guest-link page once we have one —
  // the candidate's browser only ever talks to TrustID's hosted page, never
  // its API directly.
  useEffect(() => {
    if (linkUrl && !redirecting) {
      setRedirecting(true);
      const t = setTimeout(() => {
        window.location.href = linkUrl;
      }, 900);
      return () => clearTimeout(t);
    }
  }, [linkUrl, redirecting]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <RefreshCw className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-sm p-8 text-center">
          <AlertCircle className="w-10 h-10 text-red-500 mx-auto mb-3" />
          <h1 className="text-lg font-semibold mb-1">Link unavailable</h1>
          <p className="text-sm text-slate-600 mb-5">{error}</p>
          <a
            href="/portal/login"
            className="inline-flex items-center justify-center gap-1 rounded-md px-4 py-2 text-sm font-medium text-white"
            style={{ backgroundColor: PRIMARY }}
          >
            <ChevronLeft className="w-4 h-4" /> Back to your documents
          </a>
        </div>
      </div>
    );
  }

  const isBackendError = status === 'error';
  const isTerminal = status === 'submitted_under_review' || status === 'expired' || status === 'cancelled';
  const copy = STATUS_COPY[status] || { title: 'Right to Work check', body: '' };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="verification-page">
      <header className="text-white" style={{ background: PRIMARY }}>
        <div className="max-w-lg mx-auto px-5 py-6 flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-white/15 flex items-center justify-center">
            <ShieldCheck className="w-5 h-5 text-white" />
          </div>
          <div>
            <p className="text-xs uppercase tracking-wider opacity-80">Identity Verification</p>
            <h1 className="text-lg font-semibold">Right to Work Check</h1>
          </div>
        </div>
      </header>

      <main className="max-w-lg mx-auto px-5 py-10">
        <div className="bg-white rounded-2xl shadow-sm p-8 text-center">
          {isBackendError ? (
            <>
              <AlertCircle className="w-12 h-12 text-amber-500 mx-auto mb-4" />
              <h2 className="text-xl font-semibold mb-2">Couldn't start your check</h2>
              <p className="text-sm text-slate-600 mb-6">
                {message || 'Something went wrong starting this verification. Please try again.'}
              </p>
              <button
                type="button"
                onClick={load}
                className="inline-flex items-center justify-center gap-2 rounded-lg px-4 py-2.5 text-sm font-medium text-white"
                style={{ background: PRIMARY }}
                data-testid="verification-retry-btn"
              >
                <RefreshCw className="w-4 h-4" /> Try again
              </button>
            </>
          ) : isTerminal ? (
            <>
              {status === 'submitted_under_review' ? (
                <CheckCircle2 className="w-12 h-12 mx-auto mb-4" style={{ color: PRIMARY }} />
              ) : (
                <Clock className="w-12 h-12 text-slate-400 mx-auto mb-4" />
              )}
              <h2 className="text-xl font-semibold mb-2">{copy.title}</h2>
              <p className="text-sm text-slate-600 mb-6">{copy.body}</p>
              <a
                href="/portal/login"
                className="inline-flex items-center justify-center gap-1 rounded-md px-4 py-2.5 text-sm font-medium text-white"
                style={{ backgroundColor: PRIMARY }}
                data-testid="verification-back-to-portal"
              >
                <ChevronLeft className="w-4 h-4" /> Back to all documents
              </a>
            </>
          ) : linkUrl ? (
            <>
              <ExternalLink className="w-12 h-12 mx-auto mb-4" style={{ color: PRIMARY }} />
              <h2 className="text-xl font-semibold mb-2">{copy.title}</h2>
              <p className="text-sm text-slate-600 mb-6">{copy.body}</p>
              <a
                href={linkUrl}
                className="inline-flex items-center justify-center gap-2 rounded-lg px-4 py-2.5 text-sm font-medium text-white"
                style={{ background: PRIMARY }}
                data-testid="verification-continue-link"
              >
                Continue to TrustID <ExternalLink className="w-4 h-4" />
              </a>
              <p className="text-xs text-slate-400 mt-4">Redirecting automatically…</p>
            </>
          ) : (
            <>
              <RefreshCw className="w-10 h-10 text-slate-400 mx-auto mb-4 animate-spin" />
              <h2 className="text-xl font-semibold mb-2">{copy.title}</h2>
              <p className="text-sm text-slate-600">{copy.body}</p>
            </>
          )}
        </div>
      </main>
    </div>
  );
}
